import React from 'react';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import { defineMessages, useIntl } from 'react-intl';
import * as Yup from 'yup';
import { Panel } from 'primereact/panel';
import { validationMessages } from '../../../utils/FormUtils';
import NewSuperLinkForm from './NewSuperLinkForm';
import { useSelector } from 'react-redux';
import { getUserLanguage } from '../../../utils/LanguageUtil';
import { RespondentType } from '../../../shared/Constants';

const i18nMessages = defineMessages({
  resInternal: { id: 'respondent.internal', defaultMessage: 'Internal respondents' },
  resExternal: { id: 'respondent.external', defaultMessage: 'External respondents' },
});

const NewSuperLink = ({
  onSave,
  listJobProfiles,
  reportForDropDown,
  searchJob,
  asmtTemp,
  searching,
  handleCancel,
  assessmentTemplates,
  isTempLoading,
  searchTemp,
  handleSelectTemplate,
  accountCanSelectMatchPercentageForCareerCounselingReports,
}) => {
  const { formatMessage } = useIntl();
  const { locale, translatedTags } = useSelector((state) => state.i18n);
  const viewerLocale = getUserLanguage(locale, translatedTags);

  const initialValues = {
    name: '',
    activationType: 'MANUAL',
    enabled: true,
    locale: viewerLocale.value,
    notificationEmails: [],
    printFriendlyReport: true,
    sendRespondentReport: false,
    sendRespondentCcInterestsReport: false,
    sendRespondentCcPersonalityReport: false,
    reports: [],
    ccInterestsMinimumMatchPercentage: 0,
    ccPersonalityMinimumMatchPercentage: 0,
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(formatMessage(validationMessages.required)),
    targetJob: Yup.string().required(formatMessage(validationMessages.required)),
    assessmentTemplate: Yup.string().required(formatMessage(validationMessages.required)),
    respondentType: Yup.string().required(formatMessage(validationMessages.required)),
    locale: Yup.string().required(formatMessage(validationMessages.required)),
    activationType: Yup.string().required(formatMessage(validationMessages.required)),
    numberOfAssessmentsToDisableAfter: Yup.number().when('activationType', {
      is: (activationType) => activationType === 'AUTOMATIC',
      then: Yup.number()
        .nullable()
        .required(formatMessage(validationMessages.required))
        .min(1, formatMessage(validationMessages.required)),
      otherwise: Yup.number().nullable(),
    }),
    ccInterestsMinimumMatchPercentage: Yup.number().when(['sendRespondentCcInterestsReport', 'reports'], {
      is: (sendRespondentCcInterestsReport, reports) => {
        return (
          accountCanSelectMatchPercentageForCareerCounselingReports &&
          (sendRespondentCcInterestsReport || (!!reports && reports.includes('CC_INTERESTS')))
        );
      },
      then: Yup.number()
        .required()
        .min(0)
        .max(100),
      otherwise: Yup.number().nullable(),
    }),
    ccPersonalityMinimumMatchPercentage: Yup.number().when(['sendRespondentCcPersonalityReport', 'reports'], {
      is: (sendRespondentCcPersonalityReport, reports) => {
        return (
          accountCanSelectMatchPercentageForCareerCounselingReports &&
          (sendRespondentCcPersonalityReport || (!!reports && reports.includes('CC_PERSONALITY')))
        );
      },
      then: Yup.number()
        .required()
        .min(0)
        .max(100),
      otherwise: Yup.number().nullable(),
    }),
  });

  const respondentTypeList = [
    {
      value: RespondentType.INTERNAL,
      label: formatMessage(i18nMessages.resInternal),
    },
    {
      value: RespondentType.EXTERNAL,
      label: formatMessage(i18nMessages.resExternal),
    },
  ];

  return (
    <Panel>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, formikBag) => {
          const { resetForm } = formikBag;
          onSave(values);
          resetForm();
        }}
      >
        {({ handleSubmit }) => (
          <form className="form p-col-12" onSubmit={handleSubmit}>
            <NewSuperLinkForm
              assessmentTemplates={assessmentTemplates}
              isTempLoading={isTempLoading}
              handleSelectTemplate={handleSelectTemplate}
              searchTemp={searchTemp}
              handleCancel={handleCancel}
              listJobProfiles={listJobProfiles}
              searching={searching}
              searchJob={searchJob}
              asmtTemp={asmtTemp}
              reportForDropDown={reportForDropDown}
              respondentTypeList={respondentTypeList}
              accountCanSelectMatchPercentageForCareerCounselingReports={
                accountCanSelectMatchPercentageForCareerCounselingReports
              }
            />
          </form>
        )}
      </Formik>
    </Panel>
  );
};

NewSuperLink.propTypes = {
  searching: PropTypes.bool.isRequired,
  isTempLoading: PropTypes.bool.isRequired,
  handleCancel: PropTypes.func.isRequired,
  searchJob: PropTypes.func.isRequired,
  asmtTemp: PropTypes.string.isRequired,
  searchTemp: PropTypes.func.isRequired,
  handleSelectTemplate: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  listJobProfiles: PropTypes.arrayOf(PropTypes.any).isRequired,
  reportForDropDown: PropTypes.arrayOf(PropTypes.any).isRequired,
  assessmentTemplates: PropTypes.arrayOf(PropTypes.any).isRequired,
};

export default NewSuperLink;
